import ApiHelper from "../../../common/utils/apiHelper";
import { buildAirlineMilesAndMoreBookingFields } from "../../../common/utils/transport/airlineBookingPayload";
import type { TransportAirlineApiContext } from "../../../common/utils/transport/airlineTransportContext";
import {
  buildDestinationTransportPayload,
  postDestinationTransportEstimation
} from "../../../common/utils/transport/destinationTransportEstimation";
import { globalActionCreators, selectApiBaseUrl } from "../../../common/store/modules/globalModule";
import { confirmationActionCreators } from "../../../common/store/modules/bookingConfirmationModule";
import { isEmailInvalid } from "../../../common/utils/helpers/isEmailInvalid";
import { checkForEmptyField } from "../../../common/utils/helpers/checkForEmptyFields";
import { extractFullNameParts } from "../../../common/utils/helpers/extractFullNameParts";
import { handleErrorAlert } from "../../../common/utils/helpers/handleErrorAlert";
import { DEUTSCHE_BAHN_CONFIRMATION_PATH, DEUTSCHE_BAHN_SLUG } from "./constants";
import type { DeutscheBahnBookingForm, DeutscheBahnPassenger, DeutscheBahnServiceClass } from "./types";
import { resolveDeutscheBahnApiPaths } from "./helpers/resolveDeutscheBahnApiPaths";
import {
  createInitialDeutscheBahnPassenger,
  getDeutscheBahnOfferPrices,
  syncDeutscheBahnPassengerTypes
} from "./helpers/deutscheBahnPassengerTypes";
import { getDeutscheBahnOffer } from "./helpers/getDeutscheBahnOffer";
import { mapDeutscheBahnPassengersForApi } from "./helpers/mapDeutscheBahnPassengersForApi";
import {
  getLeadGuest,
  getPassengerCategoryByTypeCode,
  getPassengerFullName,
  prefillGuestName
} from "../shared/helpers/leadGuest";
import { resolveAirportBusEstimationTime } from "../AirportBus/helpers/resolveAirportBusEstimationTime";

// Toggle mock for the Deutsche Bahn provider info GET
const USE_MOCK_DEUTSCHE_BAHN_INFO = false;
// Toggle mock for the Deutsche Bahn price estimation POST
const USE_MOCK_DEUTSCHE_BAHN_ESTIMATION = false;
// Toggle to make the mocked estimation fail with a 400 validation error (needs USE_MOCK_DEUTSCHE_BAHN_ESTIMATION = true)
const USE_MOCK_DEUTSCHE_BAHN_ESTIMATION_ERROR = false;

const MOCK_DEUTSCHE_BAHN_INFO_RESPONSE = {
  name: "Deutsche Bahn",
  type: DEUTSCHE_BAHN_SLUG,
  main: "EMBXHUD",
  highlight:
    "<p>Not tied to a specific train: travel on your chosen date, even if your flight is delayed. For trips over 100 km to participating cities, the City-Ticket for local public transport is included.</p>",
  cancellation_policy:
    "Free cancellation until the day before travel. From the day of travel, a cancellation fee of EUR 30 applies.",
  provider_title: "Deutsche Bahn",
  provider_description: "Long-distance trains (ICE and IC) from Frankfurt Airport, operated by DB Fernverkehr AG.",
  provider_logo: null,
  items: [
    {
      id: 1235,
      name: "Deutsche Bahn train ticket",
      type: DEUTSCHE_BAHN_SLUG,
      offer_type: "custom",
      code: "EMBXHUD",
      provider_name: "Deutsche Bahn",
      provider_logo: null,
      departure_address: null,
      destination_address: null,
      airport_pickup: false,
      booking_setting: null,
      time_limit: null,
      cancellation_policy:
        "Free cancellation until the day before travel. From the day of travel, a cancellation fee of EUR 30 applies.",
      offer_prices: [
        { id: 24, price: null, description: "Adult (27–64 years)", external_reference: "PNOS" },
        { id: 25, price: null, description: "Youth (15–26 years)", external_reference: "PYPO" },
        { id: 26, price: null, description: "Senior (65+ years)", external_reference: "PSOE" },
        { id: 27, price: null, description: "Child (6–14 years)", external_reference: "PCIL" },
        { id: 28, price: null, description: "Infant (0–5 years)", external_reference: "PINT" }
      ]
    }
  ],
  details: {
    custom_route_offer_code: "EMBXHUD"
  }
};

// `details.passengers` is filled from the request so the summary matches the form.
const MOCK_DEUTSCHE_BAHN_ESTIMATION_RESPONSE = {
  formatted_price: "147.75 EUR",
  miles_and_more: "739",
  details: {
    session_id: "mock-deutsche-bahn-session",
    offer_id: "mock-deutsche-bahn-offer",
    departure: {
      name: "Frankfurt(M) Flughafen Fernbf",
      latitude: 50.053,
      longitude: 8.5698
    },
    destination: {
      name: "Köln Hbf",
      street_and_number: "Trankgasse 11",
      latitude: 50.943,
      longitude: 6.9589
    },
    passengers: [] as Array<Record<string, any>>
  }
};

const MOCK_DEUTSCHE_BAHN_ESTIMATION_ERROR_RESPONSE = {
  code: 400,
  message: "Validation failed",
  debug: null,
  fields: [
    {
      field: "date",
      errorMessage: "No Deutsche Bahn connection is available for the selected date."
    }
  ]
};

export const deutscheBahnActionTypes = {
  INIT_PROVIDER: "INIT_DEUTSCHE_BAHN_PROVIDER",
  CALL_API: "CALL_DEUTSCHE_BAHN_API",
  TOGGLE_INFO_ERROR: "TOGGLE_DEUTSCHE_BAHN_INFO_ERROR",
  UPDATE_PASSENGERS_LIST: "UPDATE_DEUTSCHE_BAHN_PASSENGERS_LIST",
  SET_PREBOOK_INFO_PRICE: "SET_DEUTSCHE_BAHN_PREBOOK_INFO_PRICE",
  UPDATE_PREBOOK_API_ERROR: "UPDATE_DEUTSCHE_BAHN_PREBOOK_API_ERROR",
  UPDATE_DATA_FOR_BOOKING: "UPDATE_DEUTSCHE_BAHN_DATA_FOR_BOOKING",
  SET_BOOKING_RESPONSE_DATA: "SET_DEUTSCHE_BAHN_BOOKING_RESPONSE_DATA",
  TOGGLE_BOOKING_ERROR: "TOGGLE_DEUTSCHE_BAHN_BOOKING_ERROR",
  TOGGLE_PAYMENT_MODAL: "TOGGLE_DEUTSCHE_BAHN_PAYMENT_MODAL",
  LAST_MILE_ROUTE_LOADING: "DEUTSCHE_BAHN_LAST_MILE_ROUTE_LOADING",
  LAST_MILE_ROUTE_SUCCESS: "DEUTSCHE_BAHN_LAST_MILE_ROUTE_SUCCESS",
  LAST_MILE_ROUTE_ERROR: "DEUTSCHE_BAHN_LAST_MILE_ROUTE_ERROR",
  RESET_STATE: "RESET_DEUTSCHE_BAHN_STATE"
};

const emptyBookingForm: DeutscheBahnBookingForm = {
  nameForm: "",
  emailForm: "",
  commentForm: ""
};

export const initialState = {
  providerData: null as any,
  customRouteCode: null as string | null,
  infoError: false,
  passengerList: [] as DeutscheBahnPassenger[],
  prebookPriceInfo: null as any,
  errorPrebook: null as any,
  dataForBooking: { ...emptyBookingForm },
  // Guest name last prefilled from the lead guest; tells a prefilled name apart from one the guest typed.
  prefilledNameForm: "",
  bookingResponseData: null as any,
  bookingError: false,
  showPaymentModal: false,
  lastMileRoute: null as null | {
    distance_meters?: number | null;
    duration_seconds?: number | null;
    travel_mode?: string | null;
    google_maps_url?: string | null;
  },
  isLastMileRouteLoading: false,
  lastMileRouteError: null as string | null
};

const deutscheBahnModule = (state = initialState, action: any) => {
  switch (action.type) {
    case deutscheBahnActionTypes.INIT_PROVIDER:
      return {
        ...initialState,
        passengerList: action.passengerList
      };
    case deutscheBahnActionTypes.CALL_API:
      return {
        ...state,
        providerData: action.providerData,
        customRouteCode: action.customRouteCode,
        passengerList: action.passengerList
      };
    case deutscheBahnActionTypes.TOGGLE_INFO_ERROR:
      return { ...state, infoError: action.infoError };
    case deutscheBahnActionTypes.UPDATE_PASSENGERS_LIST:
      return { ...state, passengerList: action.passengerList };
    case deutscheBahnActionTypes.SET_PREBOOK_INFO_PRICE: {
      const nextState = { ...state, prebookPriceInfo: action.prebookPriceInfo };

      if (!action.prebookPriceInfo) {
        return nextState;
      }

      // Prefill the guest name with the lead guest when the summary opens, unless the guest typed their own.
      const leadGuest = getLeadGuest(state.passengerList, passenger =>
        getPassengerCategoryByTypeCode(passenger.passenger_type)
      );

      return prefillGuestName(nextState, getPassengerFullName(leadGuest?.first_name, leadGuest?.last_name));
    }
    case deutscheBahnActionTypes.UPDATE_PREBOOK_API_ERROR:
      return { ...state, errorPrebook: action.errorPrebook };
    case deutscheBahnActionTypes.UPDATE_DATA_FOR_BOOKING:
      return { ...state, dataForBooking: action.dataForBooking };
    case deutscheBahnActionTypes.SET_BOOKING_RESPONSE_DATA:
      return { ...state, bookingResponseData: action.bookingResponseData };
    case deutscheBahnActionTypes.TOGGLE_BOOKING_ERROR:
      return { ...state, bookingError: action.bookingError };
    case deutscheBahnActionTypes.TOGGLE_PAYMENT_MODAL:
      return { ...state, showPaymentModal: action.showPaymentModal };
    case deutscheBahnActionTypes.LAST_MILE_ROUTE_LOADING:
      return {
        ...state,
        isLastMileRouteLoading: Boolean(action.isLoading),
        ...(action.isLoading ? { lastMileRouteError: null } : {})
      };
    case deutscheBahnActionTypes.LAST_MILE_ROUTE_SUCCESS:
      return {
        ...state,
        isLastMileRouteLoading: false,
        lastMileRouteError: null,
        lastMileRoute: action.lastMileRoute ?? null
      };
    case deutscheBahnActionTypes.LAST_MILE_ROUTE_ERROR:
      return {
        ...state,
        isLastMileRouteLoading: false,
        lastMileRoute: null,
        lastMileRouteError: action.error ?? "transport.lastMileError"
      };
    case deutscheBahnActionTypes.RESET_STATE:
      return { ...initialState };
    default:
      return state;
  }
};

export default deutscheBahnModule;

export const deutscheBahnActionCreators = {
  /** Resets slice state and seeds the first passenger. Its type is set when the info GET loads `offer_prices`. */
  initDeutscheBahnAction: () => (dispatch: any) => {
    dispatch({
      type: deutscheBahnActionTypes.INIT_PROVIDER,
      passengerList: [createInitialDeutscheBahnPassenger()]
    });
  },
  getDeutscheBahnInfoAction: (transportContext: TransportAirlineApiContext) => async (dispatch: any, getState: any) => {
    const apiUrl = selectApiBaseUrl(getState());
    const paths = resolveDeutscheBahnApiPaths(transportContext);

    dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
    dispatch({ type: deutscheBahnActionTypes.TOGGLE_INFO_ERROR, infoError: false });

    if (paths.kind === "invalid_airline") {
      dispatch({ type: deutscheBahnActionTypes.TOGGLE_INFO_ERROR, infoError: true });
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      return;
    }

    try {
      let data: any;

      if (USE_MOCK_DEUTSCHE_BAHN_INFO) {
        await new Promise(resolve => setTimeout(resolve, 300));
        data = MOCK_DEUTSCHE_BAHN_INFO_RESPONSE;
      } else {
        const res = await ApiHelper.get(`${apiUrl}/${paths.infoPath}`);
        data = res?.data;
      }

      const currentPassengerList = getState().deutscheBahn.passengerList;
      const passengerList = syncDeutscheBahnPassengerTypes(
        currentPassengerList,
        getDeutscheBahnOfferPrices(getDeutscheBahnOffer(data))
      );

      dispatch({
        type: deutscheBahnActionTypes.CALL_API,
        providerData: data || null,
        customRouteCode: data?.main || data?.details?.custom_route_offer_code || null,
        passengerList
      });

      // A refetch (e.g. after a language change) replaced a passenger type the offer no longer has,
      // so a price already shown no longer matches the form.
      if (passengerList !== currentPassengerList) {
        dispatch({ type: deutscheBahnActionTypes.SET_PREBOOK_INFO_PRICE, prebookPriceInfo: null });
      }
    } catch (err: any) {
      dispatch({ type: deutscheBahnActionTypes.TOGGLE_INFO_ERROR, infoError: true });
    } finally {
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
    }
  },
  /** Walking route from the arrival station to the guest's final destination (airline transfer hub only). */
  getDeutscheBahnLastMileRouteAction:
    ({
      departureLatitude,
      departureLongitude,
      destinationPlaceId,
      travelMode
    }: {
      departureLatitude: number;
      departureLongitude: number;
      destinationPlaceId: string;
      travelMode?: "walking" | "driving" | "transit" | "bicycling" | string;
    }) =>
    async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState());
      const safePlaceId = typeof destinationPlaceId === "string" ? destinationPlaceId.trim() : "";

      if (!apiUrl || !safePlaceId) {
        return;
      }

      dispatch({ type: deutscheBahnActionTypes.LAST_MILE_ROUTE_LOADING, isLoading: true });

      try {
        const query = [
          `departureLatitude=${encodeURIComponent(String(departureLatitude))}`,
          `departureLongitude=${encodeURIComponent(String(departureLongitude))}`,
          `destinationPlaceId=${encodeURIComponent(safePlaceId)}`,
          `travelMode=${encodeURIComponent(travelMode || "walking")}`
        ].join("&");
        const res = await ApiHelper.get(`${apiUrl}/v1/airports/last-mile-route?${query}`);

        dispatch({ type: deutscheBahnActionTypes.LAST_MILE_ROUTE_SUCCESS, lastMileRoute: res?.data ?? null });
        return res?.data;
      } catch (err: any) {
        dispatch({ type: deutscheBahnActionTypes.LAST_MILE_ROUTE_ERROR, error: "transport.lastMileError" });
      } finally {
        dispatch({ type: deutscheBahnActionTypes.LAST_MILE_ROUTE_LOADING, isLoading: false });
      }
    },
  updatePassengersAction: (passengerList: DeutscheBahnPassenger[]) => (dispatch: any) => {
    dispatch({ type: deutscheBahnActionTypes.UPDATE_PASSENGERS_LIST, passengerList });
  },
  getPriceDeutscheBahnPrebookAction:
    (
      params: {
        start_date: string;
        offer_code: string | null;
        qualityOfService: DeutscheBahnServiceClass;
        milesAndMore?: string;
        departureAddress?: string | null;
        departureAddressId?: string | null;
        destinationAddress?: string | null;
        destinationAddressId?: string | null;
      },
      transportContext: TransportAirlineApiContext
    ) =>
    async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState()) || "";
      const paths = resolveDeutscheBahnApiPaths(transportContext);
      const passengers = mapDeutscheBahnPassengersForApi(getState().deutscheBahn.passengerList);

      if (paths.kind === "invalid_airline") {
        return;
      }

      // Same as Lufthansa Express Bus: no departure time is collected in the UI, but the API
      // validates that the requested date/time is not in the past, so send the current local time.
      const [datePart] = (params?.start_date ?? "").split(" ");

      const basePayload = buildDestinationTransportPayload({
        date: datePart || "",
        time: resolveAirportBusEstimationTime("current-local-time"),
        departureAddress: params?.departureAddress ?? null,
        departureAddressId: params?.departureAddressId ?? null,
        destinationAddress: params?.destinationAddress ?? null,
        destinationAddressId: params?.destinationAddressId ?? null,
        milesAndMore: params?.milesAndMore ?? "",
        offerCode: params?.offer_code ?? undefined,
        details: {
          passengers,
          qualityOfService: params?.qualityOfService
        }
      });

      const apiParams = { ...basePayload, attributes: [] };

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
      dispatch({ type: deutscheBahnActionTypes.UPDATE_PREBOOK_API_ERROR, errorPrebook: null });

      try {
        let resData: any;

        if (USE_MOCK_DEUTSCHE_BAHN_ESTIMATION) {
          await new Promise(resolve => setTimeout(resolve, 300));
          if (USE_MOCK_DEUTSCHE_BAHN_ESTIMATION_ERROR) {
            // Mimic an axios error so the real catch path handles it
            throw Object.assign(new Error("Validation failed"), {
              response: { status: 400, data: MOCK_DEUTSCHE_BAHN_ESTIMATION_ERROR_RESPONSE }
            });
          }
          resData = {
            ...MOCK_DEUTSCHE_BAHN_ESTIMATION_RESPONSE,
            details: { ...MOCK_DEUTSCHE_BAHN_ESTIMATION_RESPONSE.details, passengers }
          };
        } else {
          resData = await postDestinationTransportEstimation({
            apiUrl,
            transportContext,
            payload: apiParams,
            v2: { path: paths.estimationV2Path },
            airline: { path: paths.estimationAirlinePath }
          });
        }

        dispatch({ type: deutscheBahnActionTypes.SET_PREBOOK_INFO_PRICE, prebookPriceInfo: resData });
        return resData;
      } catch (err: any) {
        dispatch({ type: deutscheBahnActionTypes.SET_PREBOOK_INFO_PRICE, prebookPriceInfo: null });
        dispatch({
          type: deutscheBahnActionTypes.UPDATE_PREBOOK_API_ERROR,
          errorPrebook: err?.response?.data
        });
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },
  resetPrebookDataInfoAction: () => (dispatch: any) => {
    dispatch({ type: deutscheBahnActionTypes.SET_PREBOOK_INFO_PRICE, prebookPriceInfo: null });
  },
  updateFormForBookingAction: (dataForBooking: DeutscheBahnBookingForm) => (dispatch: any) => {
    dispatch({ type: deutscheBahnActionTypes.UPDATE_DATA_FOR_BOOKING, dataForBooking });
  },
  toggleBookingErrorAction: (bookingError: boolean) => (dispatch: any) => {
    dispatch({ type: deutscheBahnActionTypes.TOGGLE_BOOKING_ERROR, bookingError });
  },
  togglePaymentModalAction: (showPaymentModal: boolean) => (dispatch: any) => {
    dispatch({ type: deutscheBahnActionTypes.TOGGLE_PAYMENT_MODAL, showPaymentModal });
  },
  buyDeutscheBahnTicketAction:
    (
      params: { start_date: number; offer_code: string | null },
      navigate: any,
      transportContext: TransportAirlineApiContext
    ) =>
    async (dispatch: any, getState: any) => {
      const state = getState().deutscheBahn;
      const dataForBooking = state.dataForBooking as DeutscheBahnBookingForm;
      const passengerList = state.passengerList as DeutscheBahnPassenger[];
      const prebookPriceInfo = state.prebookPriceInfo;
      const paths = resolveDeutscheBahnApiPaths(transportContext);

      // Guest name and e-mail are mandatory; an optional comment needs at least 3 characters.
      // The name is trimmed so a whitespace-only value does not fall through to extractFullNameParts' placeholders.
      const errorField = {
        nameForm: checkForEmptyField(dataForBooking.nameForm?.trim()),
        emailForm: isEmailInvalid(dataForBooking.emailForm) || checkForEmptyField(dataForBooking.emailForm),
        commentForm: dataForBooking.commentForm !== "" && dataForBooking.commentForm?.length < 3
      };

      dispatch(deutscheBahnActionCreators.toggleBookingErrorAction(false));

      if (Object.values(errorField).some(Boolean)) {
        dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: true, errorField });
        return;
      }

      if (paths.kind === "invalid_airline") {
        dispatch(deutscheBahnActionCreators.toggleBookingErrorAction(true));
        return;
      }

      dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: false, errorField });
      localStorage.removeItem("bookingResponseDataStorage");

      const apiUrl = selectApiBaseUrl(getState());
      const language = localStorage.getItem("i18nextLng") || "en";

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));

      // The booking contact is the guest name from the summary, not the first passenger.
      const fullName = extractFullNameParts(dataForBooking.nameForm);

      const apiParams = {
        summary: {
          start_date: params?.start_date,
          adult: passengerList.length,
          offer_code: params?.offer_code
        },
        personal_information: {
          first_name: fullName.firstName,
          last_name: fullName.lastName,
          email: dataForBooking.emailForm,
          phone: "",
          comment: dataForBooking.commentForm ? dataForBooking.commentForm : null
        },
        ...buildAirlineMilesAndMoreBookingFields(transportContext),
        hotel_stay_code: getState().tenant.tenantInfo?.code,
        session_id: prebookPriceInfo?.details?.session_id,
        extraBookingData: {
          offer_id: prebookPriceInfo?.details?.offer_id
        }
      };

      try {
        const response = await ApiHelper.post(`${apiUrl}/${paths.bookingsPath}`, {}, apiParams);

        localStorage.setItem("bookingResponseDataStorage", JSON.stringify(response.data));

        // API can answer 200 with booking_status=error --> show the error page instead of Payment Modal / Confirmation
        if (response.data?.booking_status === "error") {
          dispatch(deutscheBahnActionCreators.toggleBookingErrorAction(true));
          return;
        }

        dispatch({ type: deutscheBahnActionTypes.SET_BOOKING_RESPONSE_DATA, bookingResponseData: response.data });

        // payment_status=paid (e.g. price 0) --> go straight to the confirmation page without the payment modal
        if (response.data?.payment_status === "paid") {
          await dispatch(confirmationActionCreators?.getBookingStatusAction());
          await navigate(`/${language}/${DEUTSCHE_BAHN_CONFIRMATION_PATH}`, { replace: true });
        } else {
          dispatch(deutscheBahnActionCreators.togglePaymentModalAction(true));
        }
      } catch (error: any) {
        handleErrorAlert(error);
        dispatch(deutscheBahnActionCreators.toggleBookingErrorAction(true));
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },
  resetDeutscheBahnStateAction: () => (dispatch: any) => {
    dispatch({ type: deutscheBahnActionTypes.RESET_STATE });
  }
};
